import { useParams } from 'react-router-dom';
import { Row, Col, Card, Typography, Select, Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';
import LineChart from 'src/components/chart/LineChart';
import {
  getPortfolioPerformance,
  getGreeks,
  getGreeksPnl,
  getStrategies,
  getOptionsFillRate,
} from 'src/services/account.service';
import { useEffect, useState, useContext } from 'react';
import { DateContext } from 'src/components/layout/Main';
import { formatDate } from 'src/utils/util';
import pnlChart from 'src/components/chart/configs/pnlChart';
import fillRateChart from 'src/components/chart/configs/fillRateChart';

const { Title } = Typography;

function Detail() {
  const { accountName } = useParams();
  const [strategy, setStrategy] = useState(null);
  const { timeRange } = useContext(DateContext);

  const { data: strategiesData, isLoading: strategiesLoading } = useQuery({
    queryKey: ['strategies', accountName],
    queryFn: getStrategies,
  });

  const { data: performanceData, isLoading: performanceLoading } = useQuery({
    queryKey: [
      'performance',
      accountName,
      {
        strategy_name: strategy,
        start_date: formatDate(timeRange[0]),
        end_date: formatDate(timeRange[1]),
      },
    ],
    enabled: !!strategy, // Only run this query if there are strategies available
    queryFn: getPortfolioPerformance,
  });

  const { data: greeksData, isLoading: greeksLoading } = useQuery({
    queryKey: [
      'greeks',
      accountName,
      {
        start_date: formatDate(timeRange[0]),
        end_date: formatDate(timeRange[1]),
      },
    ],
    queryFn: getGreeks,
  });

  const { data: greeksPnlData, isLoading: greeksPnlLoading } = useQuery({
    queryKey: [
      'greeksPnl',
      accountName,
      {
        start_date: formatDate(timeRange[0]),
        end_date: formatDate(timeRange[1]),
      },
    ],
    queryFn: getGreeksPnl,
  });

  const { data: optionsFillRate, isLoading: optionsFillRateLoading } = useQuery(
    {
      queryKey: [
        'optionsFillRate',
        accountName,
        {
          start_date: formatDate(timeRange[0]),
          end_date: formatDate(timeRange[1]),
        },
      ],
      queryFn: getOptionsFillRate,
    }
  );

  useEffect(() => {
    if (strategiesData && strategiesData?.result.length > 0) {
      setStrategy(strategiesData.result[0]);
    } else {
      setStrategy(null);
    }
  }, [strategiesLoading, strategiesData]);

  const StrategySelect =
    strategiesData && strategiesData?.result.length === 0 ? (
      <Select className='select-option' disabled>
        <Select.Option value=''>No Strategies Available</Select.Option>
      </Select>
    ) : (
      strategiesData &&
      strategiesData?.result.length > 0 && (
        <Select
          className='select-option'
          defaultValue={strategiesData?.result[0]}
          onChange={(value) => {
            setStrategy(value);
            // queryClient.invalidateQueries(["performance", accountName, { strategy_name: value, start_date: "2025-06-01T00:00:00", end_date: "2025-06-02T01:00:00" }]);
          }}
        >
          {strategiesData &&
            strategiesData?.result.map((strategy, index) => (
              <Select.Option key={index} value={strategy}>
                {strategy}
              </Select.Option>
            ))}
        </Select>
      )
    );
  return (
    <>
      <div className='layout-content'>
        <Row gutter={[24, 0]}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24} className='mb-24'>
            <Spin
              spinning={strategiesLoading || performanceLoading}
              tip='Loading...'
            >
              <Card bordered={false} className='criclebox h-full'>
                <LineChart
                  title='AP vs MP'
                  series={performanceData?.result}
                  topRightChildren={StrategySelect}
                  options={pnlChart.options}
                />
              </Card>
            </Spin>
          </Col>
        </Row>

        <Row gutter={[24, 0]}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24} className='mb-24'>
            <Spin spinning={optionsFillRateLoading} tip='Loading...'>
              <Card bordered={false} className='criclebox h-full'>
                <LineChart
                  title='Options Fill Rate'
                  series={optionsFillRate?.result}
                  options={fillRateChart.options}
                />
              </Card>
            </Spin>
          </Col>
        </Row>

        <div className='Header-top'>
          <Title level={5} className='mb-0'>
            Greeks
          </Title>
        </div>

        <Spin spinning={greeksLoading} tip='Loading...'>
          <Row gutter={[24, 0]}>
            <Col xs={24} sm={24} md={12} lg={12} xl={12} className='mb-24'>
              <Card bordered={false} className='criclebox h-full'>
                <LineChart
                  title='Delta'
                  series={greeksData?.result?.net_delta}
                  options={pnlChart.options}
                />
              </Card>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={12} className='mb-24'>
              <Card bordered={false} className='criclebox h-full'>
                <LineChart
                  title='Gamma'
                  series={greeksData?.result?.gamma}
                  options={pnlChart.options}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[24, 0]}>
            <Col xs={24} sm={24} md={12} lg={12} xl={12} className='mb-24'>
              <Card bordered={false} className='criclebox h-full'>
                <LineChart
                  title='Theta'
                  series={greeksData?.result?.theta}
                  options={pnlChart.options}
                />
              </Card>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={12} className='mb-24'>
              <Card bordered={false} className='criclebox h-full'>
                <LineChart
                  title='Vega'
                  series={greeksData?.result?.vega}
                  options={pnlChart.options}
                />
              </Card>
            </Col>
          </Row>
        </Spin>

        <div className='Header-top'>
          <Title level={5} className='mb-0'>
            Greeks PnL
          </Title>
        </div>

        <Spin spinning={greeksPnlLoading} tip='Loading...'>
          <Row gutter={[24, 0]}>
            <Col xs={24} sm={24} md={12} lg={12} xl={12} className='mb-24'>
              <Card bordered={false} className='criclebox h-full'>
                <LineChart
                  title='Delta PnL'
                  series={greeksPnlData?.result?.net_delta_pnl}
                  options={pnlChart.options}
                />
              </Card>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={12} className='mb-24'>
              <Card bordered={false} className='criclebox h-full'>
                <LineChart
                  title='Gamma PnL'
                  series={greeksPnlData?.result?.gamma_pnl}
                  options={pnlChart.options}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[24, 0]}>
            <Col xs={24} sm={24} md={12} lg={12} xl={12} className='mb-24'>
              <Card bordered={false} className='criclebox h-full'>
                <LineChart
                  title='Theta PnL'
                  series={greeksPnlData?.result?.theta_pnl}
                  options={pnlChart.options}
                />
              </Card>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={12} className='mb-24'>
              <Card bordered={false} className='criclebox h-full'>
                <LineChart
                  title='Vega PnL'
                  series={greeksPnlData?.result?.vega_pnl}
                  options={pnlChart.options}
                />
              </Card>
            </Col>
          </Row>
        </Spin>
      </div>
    </>
  );
}

export default Detail;
